/**
 * @module Tabs
 */

import { getKeyName } from '../../../scripts/helpers';
import Component from '../Component';
import './styles.css';

/**
 * Состояние панели табов.
 * @typedef {Object} TabsState
 *
 * @property {number} index Индекс раскрытой панели.
 * @property {jQuery} $item Заголовок таба.
 * @property {jQuery} $panel Панель таба.
 * @property {string} itemId id заголовка таба.
 * @property {string} panelId id панели таба.
 */

/**
 * Табы.
 *
 * @todo Реализовать кастомные эвенты открытия панелей.
 * @todo Подумать о типовых перестраиваниях на адаптиве.
 * @todo Реализовать faketabs со ссылками вместо кнопок.
 * @todo Добавить поддержку disabled.
 */
export default class Tabs extends Component {
    static className = 'tabs';

    static keyCodes = {
        END: 35,
        HOME: 36,
        LEFT: 37,
        RIGHT: 39,
    };

    initElements() {
        this.elements = {
            $itemsList: $('.tabs__items', this.$block),
            $items: $('.tabs__item', this.$block),
            $panelsList: $('.tabs__panels', this.$block),
            $panels: $('.tabs__panel', this.$block),
        };
    }

    setProperties({ selected = 0 }) {
        /**
         * Cостояние активной панели табов.
         * @type {TabsState}
         */
        this.state = {
            index: selected,
        };
    }

    init(options, pluginOptions) {
        super.init(options, pluginOptions);
        this.initStates();
        this.addAdditionalMarkup();
        const videoFrame = this.state.$panel.find('iframe');
        if (videoFrame.length) {
            videoFrame.attr('src', this.state.$item.data('video-src'));
        }
    }

    /**
     * Заполняет данные состояний панелей.
     */
    initStates() {
        const $item = this.elements.$items.eq(this.state.index);
        const $panel = this.elements.$panels.eq(this.state.index);

        this.state = {
            ...this.state,
            $item,
            $panel,
            itemId: $item.attr('id'),
            panelId: $panel.attr('id'),
        };
    }

    /**
     * Прописывает дополнительную разметку.
     */
    addAdditionalMarkup() {
        /* Добавляем роли */
        this.elements.$itemsList.attr('role', 'tablist');
        this.elements.$items.attr('role', 'tab');
        this.elements.$panels.attr('role', 'tabpanel');

        /* Добавляем ARIA-состояния и классы */
        this.elements.$items.not(this.state.$item).attr('aria-selected', 'false');
        this.state.$item.attr('aria-selected', 'true');
        this.state.$item.addClass('tabs__item--selected');
        this.elements.$panels.not(this.state.$panel).addClass('tabs__panel--hidden');

        /* Реализуем паттерн roving tabindex */
        this.elements.$items.not(this.state.$item).attr('tabindex', '-1');

        /* Добавляем ARIA-атрибуты */
        this.elements.$items.each((index, item) => {
            const $itemBtn = $(item);
            const $panel = this.elements.$panels.eq(index);

            $itemBtn.attr('aria-controls', $panel.attr('id'));
            $panel.attr('aria-labelledby', $itemBtn.attr('id'));
        });
    }

    /**
     * Открывает панель.
     *
     * @param {number} index Номер открываемой панели.
     */
    openPanel(index) {
        /* При попытке открыть панель, которая уже открыта, ничего не делаем. */
        if (index === this.state.index) return;

        const $item = this.elements.$items.eq(index);
        const $panel = this.elements.$panels.eq(index);

        const videoFrame = $panel.find('iframe');
        if (videoFrame.length && !videoFrame.attr('src')) {
            videoFrame.attr('src', $item.data('video-src'));
        }

        /* Закрываем текущую панель */
        this.state.$item.removeClass('tabs__item--selected');
        this.state.$item.attr('aria-selected', 'false');
        this.state.$panel.addClass('tabs__panel--hidden');

        /* Открываем указанную панель */
        $item.addClass('tabs__item--selected');
        $item.attr('aria-selected', 'true');
        $panel.removeClass('tabs__panel--hidden');

        /* Реализуем паттерн roving tabindex */
        this.state.$item.attr('tabindex', '-1');
        $item.removeAttr('tabindex');
        $item[0].focus();

        /* Обновляем состояние */
        this.state = {
            index,
            $item,
            $panel,
            itemId: $item.attr('id'),
            panelId: $panel.attr('id'),
        };
    }
}

$(document).on('click', '.tabs__item', handleItemClick);
$(document).on('keydown', '.tabs__item', handleItemKeydown);

function handleItemClick(e) {
    const { currentTarget } = e;
    const $item = $(currentTarget);
    const $block = $item.closest('.tabs');
    const tabs = Tabs.getInstance($block);

    const index = tabs.elements.$items.toArray().findIndex(item => $(item).is($item));

    tabs.openPanel(index);
}

function handleItemKeydown(e) {
    const { currentTarget, keyCode } = e;
    const $item = $(currentTarget);
    const key = getKeyName(Tabs.keyCodes, keyCode);

    if (!key) return;

    const $block = $item.closest('.tabs');
    const tabs = Tabs.getInstance($block);

    const { index } = tabs.state;
    const tabsCount = tabs.elements.$items.length;

    let newIndex;

    if (['LEFT', 'RIGHT'].includes(key)) {
        const direction = key === 'LEFT' ? -1 : 1;
        newIndex = (tabsCount + index + direction) % tabsCount;
    } else if (key === 'END') {
        newIndex = tabsCount - 1;
    } else if (key === 'HOME') {
        newIndex = 0;
    }

    tabs.openPanel(newIndex);

    e.preventDefault();
}
